/* iter68 — Bulletin Signature Campaign card for the DirectorDashboard.
 * Displays campaign KPIs for the selected class/period, plus a "Send to
 * parents" button that batch-creates signature records and pushes emails.
 */
import { useEffect, useState } from 'react';
import { bulletinSignaturesApi } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Envelope, PaperPlaneTilt, CheckCircle, ArrowClockwise, ChartPieSlice,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const fmtDuration = (hours) => {
  if (hours == null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} j`;
};

export default function BulletinSignatureCampaign({ className, periodKey, schoolYear }) {
  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);

  const load = async () => {
    if (!className || !periodKey) return;
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        bulletinSignaturesApi.campaignStats({
          className, periodKey, schoolYear,
        }).then((r) => r.data),
        bulletinSignaturesApi.listLinks({
          className, periodKey, schoolYear, limit: 100,
        }).then((r) => r.data),
      ]);
      setStats(s);
      setLinks(l.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement campagne');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [className, periodKey, schoolYear]);

  const sendToParents = async () => {
    if (!window.confirm(
      `Générer les liens de signature et notifier les parents de ${className} ` +
      `pour la période ${periodKey} ?`,
    )) return;
    setSending(true);
    try {
      const { data } = await bulletinSignaturesApi.sendClassLinks({
        class_name: className, period_key: periodKey, school_year: schoolYear,
      });
      toast.success(
        `${data.created} nouveau(x), ${data.reused} déjà créés — ` +
        `${data.notified} élève(s) notifiés`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  const triggerReminders = async () => {
    setReminding(true);
    try {
      const { data } = await bulletinSignaturesApi.sendReminders();
      toast.success(`Relances envoyées : ${data.sent} (J+2/J+5)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur relance');
    } finally { setReminding(false); }
  };

  const rate = stats?.signature_rate ?? 0;
  const rateColor = rate >= 80 ? 'text-emerald-600'
    : rate >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid="bulletin-signature-campaign">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <Envelope size={18} className="text-[#0052FF]" weight="duotone" />
          Campagne de signatures bulletins
        </CardTitle>
        <div className="flex items-center gap-2">
          <Link
            to="/bulletin-compliance"
            className="text-xs text-[#0052FF] hover:underline flex items-center gap-1"
            data-testid="link-bulletin-compliance"
          >
            <ChartPieSlice size={14} /> Voir Compliance
          </Link>
          <Button
            size="sm" variant="outline" onClick={load} disabled={loading}
            data-testid="bul-campaign-refresh"
          >
            <ArrowClockwise size={14} className="mr-1" />
            {loading ? '…' : 'Actualiser'}
          </Button>
          <Button
            size="sm" variant="outline" onClick={triggerReminders}
            disabled={reminding || !stats?.pending}
            data-testid="bul-trigger-reminders"
          >
            {reminding ? '…' : 'Relancer'}
          </Button>
          <Button
            size="sm" onClick={sendToParents}
            disabled={sending || !className || !periodKey}
            className="bg-[#0052FF] hover:bg-[#0043D1]"
            data-testid="bul-send-to-parents"
          >
            <PaperPlaneTilt size={14} className="mr-1" />
            {sending ? 'Envoi…' : 'Envoyer aux parents'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!className || !periodKey ? (
          <p className="text-sm text-[#94A3B8]">
            Sélectionnez une classe et une période pour afficher la campagne.
          </p>
        ) : (
          <>
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                <p className="text-[10px] uppercase text-[#64748B] font-bold">Bulletins</p>
                <p className="text-2xl font-outfit font-bold" data-testid="bul-kpi-total-scoped">
                  {stats?.total ?? 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-[10px] uppercase text-emerald-700 font-bold">Signés (3/3)</p>
                <p className="text-2xl font-outfit font-bold text-emerald-700"
                  data-testid="bul-kpi-signed-scoped">
                  {stats?.fully_signed ?? 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-[10px] uppercase text-amber-700 font-bold">En attente</p>
                <p className="text-2xl font-outfit font-bold text-amber-700"
                  data-testid="bul-kpi-pending-scoped">
                  {stats?.pending ?? 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                <p className="text-[10px] uppercase text-[#64748B] font-bold">Taux</p>
                <p className={`text-2xl font-outfit font-bold ${rateColor}`}
                  data-testid="bul-kpi-rate-scoped">
                  {rate}%
                </p>
              </div>
            </div>

            {/* Partial signatures summary */}
            {stats && stats.total > 0 && (
              <div className="text-xs text-[#64748B]">
                <span className="mr-3">Partiel :</span>
                <span className="font-medium">Parent {stats.partial_signatures?.parent ?? 0}</span>
                <span className="mx-1">•</span>
                <span className="font-medium">Prof {stats.partial_signatures?.teacher ?? 0}</span>
                <span className="mx-1">•</span>
                <span className="font-medium">Direction {stats.partial_signatures?.director ?? 0}</span>
                <span className="mx-3 text-[#94A3B8]">|</span>
                <span>Relancés : <b>{stats.reminded ?? 0}</b></span>
              </div>
            )}

            {/* iter69 — SLA de signature (temps moyen jusqu'à 3/3) */}
            {stats && (stats.sla?.count ?? 0) > 0 && (
              <div
                className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3"
                data-testid="bul-sla-card"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-[10px] uppercase text-[#64748B] font-bold">
                      SLA de signature (temps jusqu'à 3/3)
                    </p>
                    <p className="text-xs text-[#64748B]">
                      Basé sur {stats.sla.count} bulletin(s) signé(s).
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <p className="text-[10px] uppercase text-[#64748B]">Moyen</p>
                      <p className="font-outfit text-lg font-bold text-[#0052FF]"
                        data-testid="bul-sla-avg">
                        {fmtDuration(stats.sla.avg_hours)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-[#64748B]">Médian</p>
                      <p className="font-outfit text-lg font-bold text-emerald-700"
                        data-testid="bul-sla-median">
                        {fmtDuration(stats.sla.median_hours)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-[#64748B]">P90</p>
                      <p className="font-outfit text-lg font-bold text-amber-700"
                        data-testid="bul-sla-p90">
                        {fmtDuration(stats.sla.p90_hours)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Table */}
            {links.length === 0 ? (
              <p className="text-sm text-[#94A3B8] italic"
                data-testid="bul-campaign-empty">
                Aucun lien de signature créé pour {className} · {periodKey}. Cliquez sur
                « Envoyer aux parents » pour lancer la campagne.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bul-campaign-table">
                  <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                    <tr>
                      <th className="text-left p-2">Élève</th>
                      <th className="text-center p-2">Statut</th>
                      <th className="text-center p-2">Parent</th>
                      <th className="text-center p-2">Prof</th>
                      <th className="text-center p-2">Direction</th>
                      <th className="text-center p-2">Créé le</th>
                    </tr>
                  </thead>
                  <tbody>
                    {links.map((r) => {
                      const sigs = r.signatures || {};
                      const done = sigs.parent && sigs.teacher && sigs.director;
                      return (
                        <tr key={r.id}
                          className="border-b border-[#F1F5F9]"
                          data-testid={`bul-campaign-row-${r.id}`}>
                          <td className="p-2">{r.student_name}</td>
                          <td className="p-2 text-center">
                            {done ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs">
                                <CheckCircle size={14} weight="fill" /> Signé
                              </span>
                            ) : (
                              <span className="text-amber-700 text-xs font-semibold">
                                {Object.values(sigs).filter(Boolean).length}/3
                              </span>
                            )}
                          </td>
                          {['parent', 'teacher', 'director'].map((role) => (
                            <td key={role} className="p-2 text-center">
                              {sigs[role] ? (
                                <CheckCircle size={16} weight="fill"
                                  className="text-emerald-600 inline" />
                              ) : (
                                <span className="text-[#CBD5E1]">—</span>
                              )}
                            </td>
                          ))}
                          <td className="p-2 text-center text-xs text-[#64748B]">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR')
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
